import {createContext, useContext} from 'react';

import {
    AssignedEntity,
    BrowseAggregatorContent,
    BrowseAggregatorEntityType,
    BrowsePageContent,
    BrowsePageContentUtil,
    CertificationBrowseAggregatorContentUtil,
} from '@udemy/browse-aggregator';

export interface BaseBrowseAggregatorContext {
    browsePageContent: BrowsePageContent;
    aggregatorContent?: BrowseAggregatorContent;
}

export interface CertificationBrowseAggregatorContext extends BaseBrowseAggregatorContext {
    assignedEntities?: {
        topicDescriptor?: AssignedEntity;
        topic?: AssignedEntity;
        searchQuery?: AssignedEntity;
        toRelatedAggregator?: AssignedEntity;
    };
    entityForRelatedAggregator?: AssignedEntity;
    aggregatorEnrollmentStat?: number;
    isIssuerAggregator?: boolean;
}

export const BrowseAggregatorContext = createContext<{
    browsePageContent: BrowsePageContent;
}>({browsePageContent: {} as BrowsePageContent});

export function buildCertificationBrowseAggregatorContext(browsePageContent?: BrowsePageContent) {
    if (!browsePageContent?.contents) {
        return undefined;
    }
    const browsePageContentUtil = new BrowsePageContentUtil(browsePageContent);
    const aggregatorContent = browsePageContentUtil.getBrowseAggregatorContent();

    let context: CertificationBrowseAggregatorContext = {
        browsePageContent,
        aggregatorEnrollmentStat: browsePageContentUtil.getEnrollmentStat(),
    };

    if (aggregatorContent) {
        const certificationAggregatorUtil = new CertificationBrowseAggregatorContentUtil(
            aggregatorContent,
        );
        const isIssuerAggregator = certificationAggregatorUtil.isIssuerAggregator();
        context = {
            ...context,
            aggregatorContent,
            assignedEntities: {
                topicDescriptor: certificationAggregatorUtil.getAssignedEntityByType(
                    isIssuerAggregator
                        ? BrowseAggregatorEntityType.TOPIC_DESCRIPTOR_ISSUER
                        : BrowseAggregatorEntityType.TOPIC_DESCRIPTOR_SUBJECT,
                ),
                topic: certificationAggregatorUtil.getAssignedEntityByType(
                    BrowseAggregatorEntityType.TOPIC,
                ),
                searchQuery: certificationAggregatorUtil.getAssignedEntityByType(
                    BrowseAggregatorEntityType.SEARCH_QUERY,
                ),
            },
            entityForRelatedAggregator:
                certificationAggregatorUtil.getAssignedEntityForRelatedAggregator(),
            isIssuerAggregator: isIssuerAggregator,
        };
    }

    return context;
}

export function useCertificationBrowseAggregatorContext():
    | CertificationBrowseAggregatorContext
    | undefined {
    const {browsePageContent} = useContext(BrowseAggregatorContext);
    return buildCertificationBrowseAggregatorContext(browsePageContent);
}
